import { ChangeDetectorRef, Component, HostListener, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from './core/http/auth/auth.service';
import { ThemeService } from 'src/app/core/services/theme.service';
import { LoaderScreenService } from './core/services/loaderScreen.service';
import { PasswordExpiryService } from './core/services/password-expiry.service';
import { config as siteConfig } from 'src/sites/config';
import { Title } from '@angular/platform-browser';
import { LoaderConfigService } from './core/services/loader-config.service';
import { Subject } from 'rxjs';
import { GlobalService } from 'src/app/core/services/global.service';
import { AMTDomains, publicRoutes } from 'src/app/shared/models/constants';
import { ToastrService } from "ngx-toastr";
import { ErrorConstants } from 'src/app/shared/models/errorConstants';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css']
})

export class AppComponent implements OnDestroy {
    configurations: any = {};
    title = 'Member Portal';
    topBtn: boolean = false;
    isQuickEnroll: boolean = false;
    private readonly QUICK_ENROLL_PATH = 'quick-enroll';
    private destroy$ = new Subject<void>();
    showPasswordExpiryPopup = false;
    passwordExpiryDays = 0;
    constructor(
        private router: Router,
        private authService: AuthService,
        private themeService: ThemeService,
        public loaderScreenService: LoaderScreenService,
        private titleService: Title,
        private loaderConfigService: LoaderConfigService,
        private cdr: ChangeDetectorRef,
        private globalService: GlobalService,
        private passwordExpiryService: PasswordExpiryService,
        private toastr: ToastrService,
    ) {
        this.configurations = siteConfig;
    }

    ngOnInit() {
        this.loaderConfigService.setQuickEnrollPageState(true);
        if (this.isAuthPage()) {
            this.loaderConfigService.setQuickEnrollPageState(false);
        }
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).pipe(takeUntil(this.destroy$)).subscribe(() => {
            const currentUrl = this.router.url;
            this.isQuickEnroll = currentUrl.includes(this.QUICK_ENROLL_PATH);
            this.loaderConfigService.setQuickEnrollPageState(this.isQuickEnroll);
            this.loaderScreenService.config.logoUrl = this.isQuickEnroll ? '' : this.configurations.logoUrl;
            if (!this.isQuickEnroll) {
                this.loaderConfigService.setQuickEnrollPageState(false);
            }
            this.cdr.detectChanges();
        });

        this.title = this.configurations.configuration.siteTitle;
        this.titleService.setTitle(this.title);
        this.loaderConfigService.isQuickEnrollPage$.subscribe(isQuickEnrollPage => {
            this.themeService.setTheme().then(() => {
                this.loaderScreenService.setLoaderConfigurations(isQuickEnrollPage).then((response: any) => {
                    this.cdr.detectChanges();
                });
            });
        });

        this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe((event: NavigationStart) => {
            this.globalService.loaderStart();
            // Check if pass code verified
            if (event.url !== '/passcode-verification' && event.url !== '/login' && event.url !== '/login/member') {
                this.authService.checkPasscodeVerified(event.url);
            }
            // TASK: IES-3736 
            // This handles the scenario where a user attempts to access a specific route after their session has expired.
            if (publicRoutes.includes(event.url)) {
                localStorage.setItem('url', event.url);
            }
        });

        this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(event => {
            //Check app pre-events
            if (AMTDomains.indexOf(location.host) !== -1 && event['url'] == '/login') {
                this.router.navigate(['/login/member']);
            }

            // Added code to redirect to change password page if forcePasswordReset is true
            if ((event['url'] != '/change-password') && !publicRoutes.includes(event['url']) && this.globalService?.user?.forcePasswordReset) {
                this.toastr.warning(ErrorConstants.ERROR_PASSWORD_EXPIRED);
                this.router.navigate(['/change-password']);
            }
            if (event['url'] != '/change-password' && event['url'] != '/passcode-verification' && !publicRoutes.includes(event['url']) && !this.globalService?.user?.forcePasswordReset && !this.globalService?.user?.firstLogin) {
                this.passwordExpiryService.passwordExpiry$.pipe(takeUntil(this.destroy$)).subscribe(expiry => {
                    this.showPasswordExpiryPopup = expiry.show;
                    this.passwordExpiryDays = expiry.days;
                });
            }
            //Check FirstLogin
            this.authService.checkFirstLogin(event['url']);
            this.globalService.loaderStop();
        });

        window.addEventListener('storage', (event) => {
            if (event.storageArea == localStorage) {
                let token = this.authService.getToken();
                if (token == undefined) {
                    const currentUrl = this.router.url;
                    this.isQuickEnroll = currentUrl.includes(this.QUICK_ENROLL_PATH);
                    /* IES-3043: Quick Enroll loading login page issue */
                    /* IES-4031: Added public routes check to avoid redirecting to login page when user is on public routes */
                    if (!publicRoutes.includes(currentUrl)) {
                        this.authService.deleteToken();
                    }
                }
            }
        });
    }

    isAuthPage() {
        const url = window.location.href;
        return url.includes('login') || url.includes('forgot-password');
    }

    @HostListener("window:scroll", []) onWindowScroll() {
        this.scrollFunction();
    }

    scrollFunction() {
        if (document.body.scrollTop > 20 || document.documentElement.scrollTop > 20) {
            this.topBtn = true;
        } else {
            this.topBtn = false;
        }
        //Sticky
        var header = document.getElementById("wrap-sticky-header");
        if (header) {
            var sticky = header.offsetTop;
            if (window.pageYOffset > sticky) {
                header.classList.add("sticky");
            } else {
                header.classList.remove("sticky");
            }
        }
    }

    topFunction() {
        window.scroll({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    }

    onChangePassword() {
        this.passwordExpiryService.hidePopup();
        this.router.navigate(['/change-password']);
    }

    onRemindLater() {
        this.passwordExpiryService.setRemindLater();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
